import { PLACEHOLDER } from '@/components/review/FindingOverlay';
import type { CategorySummary, ExportMode, FindingModel } from '@/types/documents.types';
import { UNKNOWN_CATEGORY_COLOR, categoryLabel, categoryTint } from '@/utils/categories';
import { findingItemName, findingMethod } from '@/utils/findings';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface FindingRowProps {
  finding: FindingModel;
  /** The finding's categories in the session snapshot (unknown keys already dropped). */
  categories: CategorySummary[];
  /** The category the row is coloured in (`findingDisplayCategory`). */
  display: CategorySummary | undefined;
  pageNumber: number;
  /** The review's view mode; `anonymized` shows the finding's substitute under its value. */
  mode: ExportMode;
  /** True when the export acts on this finding. */
  inExport: boolean;
  selected: boolean;
  isSaving: boolean;
  disabled?: boolean;
  onSelect: (findingId: string) => void;
  onToggle: (finding: FindingModel) => void;
}

/**
 * One row of "Detekované údaje": the category bar, the value, the item-name tag in the colour of the
 * finding's display category, the meta line "{method} · str. N", and "Ignorovat" / "Vrátit".
 *
 * A row the export leaves alone (ignored itself, or all of its categories off) is faded and struck
 * through; the button still works on the finding itself.
 */
export const FindingRow: FC<FindingRowProps> = ({
  finding,
  categories,
  display,
  pageNumber,
  mode,
  inExport,
  selected,
  isSaving,
  disabled = false,
  onSelect,
  onToggle,
}) => {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLLIElement>(null);
  const isExcluded = finding.status === 'excluded';
  const ignored = !inExport;
  const color = display?.color || UNKNOWN_CATEGORY_COLOR;

  // Selecting a box on the page brings its row into view, or the panel looks unchanged.
  useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);

  const method = findingMethod(finding, t);
  const meta = [method, t('review.findings.pageShort', { page: pageNumber })].join(' · ');
  // The prototype's "kind": the item's name; declarations and manual findings have no item, so
  // their method ("deklarace", "ručně") names them instead.
  const kind = findingItemName(finding) || method;
  const categoryNames = categories.map((category) => categoryLabel(category.key, category.label_cs)).join(', ');

  return (
    <li
      ref={rowRef}
      className={`flex items-start gap-[9px] border-b border-hairline-2 px-2 py-2.5 ${selected ? 'bg-surface' : ''}`}
      style={{ opacity: ignored ? 0.55 : 1 }}
    >
      <span
        className="min-h-[30px] w-[3px] flex-none self-stretch"
        style={{ background: ignored ? '#cfd9e2' : color }}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={() => onSelect(finding.id)}
        className="min-w-0 flex-1 cursor-pointer border-none bg-transparent p-0 text-left font-[inherit]"
      >
        <span
          className={`block break-words text-[12.5px] font-semibold ${ignored ? 'text-subtle' : 'text-ink'} ${
            isExcluded || ignored ? 'line-through' : ''
          }`}
        >
          {finding.text}
        </span>
        {mode === 'anonymized' && inExport && (
          <span title={t('review.finding.substitute')} className="mt-0.5 block break-words text-[11.5px] text-subtle">
            → {finding.substitute || PLACEHOLDER}
          </span>
        )}
        <span className="mt-[3px] flex flex-wrap items-center gap-1.5">
          <span
            title={categoryNames}
            className="px-1.5 py-0.5 text-[10px] font-semibold tracking-[.02em]"
            style={
              ignored
                ? { background: '#eef3f7', color: '#8194a6' }
                : { background: display?.tint || categoryTint(color), color }
            }
          >
            {kind}
          </span>
          {/* A finding of a multi-category item: one small square per further category. */}
          {categories
            .filter((category) => category.key !== display?.key)
            .map((category) => (
              <span
                key={category.key}
                title={categoryLabel(category.key, category.label_cs)}
                className="inline-block h-[7px] w-[7px] flex-none"
                style={{ background: ignored ? '#cfd9e2' : category.color }}
                aria-hidden="true"
              />
            ))}
          <span className="sr-only">{categoryNames}</span>
          <span className="text-[10.5px] text-faint">{meta}</span>
        </span>
      </button>
      <button
        type="button"
        disabled={isSaving || disabled}
        onClick={() => onToggle(finding)}
        className={`flex-none cursor-pointer rounded border border-control bg-white px-[9px] py-[5px] text-[11px] font-semibold disabled:cursor-progress ${
          isExcluded ? 'text-primary' : 'text-muted'
        }`}
      >
        {isExcluded ? t('review.finding.restore') : t('review.finding.exclude')}
      </button>
    </li>
  );
};
